import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Divider, Switch, Tooltip, Alert, Button, Input, Space } from 'antd';
import { v4 as uuid } from 'uuid';
import { CopyOutlined } from '@ant-design/icons';
import { ZorbaButton, ZorbaSelect } from '../../../../components/forms';
import {
  BUTTON_VARIANT_DARK,
  BUTTON_VARIANT_LINK,
} from '../../../../components/forms/ZorbaButton/constants';
import { setModalState } from '../../../../reducers/Modal/actions';
import { MODAL_EDIT_FIELD } from '../../../../reducers/Modal/types';
import { updateBuyerDetails } from '../../../../reducers/Buyer/actions';
import { EditFieldModal } from '../EditFieldModal';
import { Webhooks } from '../Webhooks';
import { getNotificationsFieldsToEdit } from './constants';
import {
  showErrorToast,
  showSuccessToast,
} from '../../../../services/notificationsService';
import { isValidHttpUrl } from '../../../../services/urlService';
import { copy } from '../../../../services/clipboardService';
import { postRequest } from '../../../../services/requestService';
import { getUserData } from '../../../../reducers/Auth/actions';
import { Spinner } from '../../../../components/Spinner';

export const ApiIntegration = () => {
  const TRICON_ORGANIZATION_ID = '67599faa2e2b696b2f5c01f7';
  const dispatch = useDispatch();
  const { organization, isAppLoading } = useSelector(
    ({ AuthReducer }) => AuthReducer,
  );
  const { isLoading } = useSelector(({ BuyerReducer }) => BuyerReducer);
  const [selectedField, setSelectedField] = useState();
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);
  const { webhooks } = organization.buyerConfig;

  const fields = getNotificationsFieldsToEdit({
    webhooks,
  });

  const handleEdit = (field) => {
    setSelectedField(field);
    if (
      field.fieldName === 'newDeals' ||
      field.fieldName === 'dealFollowUps' ||
      field.fieldName === 'eodReport'
    ) {
      dispatch(setModalState(MODAL_EDIT_FIELD, true));
    }
  };

  const handleModalClose = () => {
    setSelectedField();
    dispatch(setModalState(MODAL_EDIT_FIELD, false));
  };

  const handleEmailSwitchChange = (checked) => {
    const fieldToConfigMap = {
      newDeals: 'unsubscribeFromNewDealsEmail',
      eodReport: 'unsubscribeFromEodEmail',
      dealFollowUps: 'unsubscribeFromOfferFollowUps',
    };

    const configField = fieldToConfigMap[selectedField.fieldName];
    if (!configField) return;

    setSelectedField({
      ...selectedField,
      emailValue: checked,
    });

    dispatch(
      updateBuyerDetails(
        {
          buyerConfig: {
            ...organization.buyerConfig,
            [configField]: !checked,
          },
        },
        'Notification settings updated successfully!',
        'Failed to update notification settings.',
      ),
    );
  };

  const notificationsRecipientsOptions = organization.members.map((member) => ({
    text: member.email,
    value: member.email,
  }));

  const handleCancel = () => {
    setSelectedField(null);
  };

  const handleSaveGlobalNotificationRecipients = () => {
    dispatch(
      updateBuyerDetails(
        {
          globalNotificationsRecipients: selectedField.value,
        },
        'Notification settings updated successfully!',
        'Failed to update notification settings.',
        () => handleCancel(),
      ),
    );
  };

  const setWebhooks = (updatedWebhooks) => {
    setSelectedField({
      ...selectedField,
      value: updatedWebhooks,
    });
  };

  const handleSaveWebhooks = () => {
    const isValid =
      selectedField.value.filter((webhook) => {
        const hasNoEvents = !webhook.events?.length;
        const urlIsInvalid = !isValidHttpUrl(webhook.url);
        return hasNoEvents || urlIsInvalid;
      }).length > 0;

    if (isValid) {
      showErrorToast(
        'Please check your settings; they appear to be invalid. Correct them and submit again.',
      );
    } else {
      dispatch(
        updateBuyerDetails(
          {
            buyerConfig: {
              ...organization.buyerConfig,
              webhooks: selectedField.value,
            },
          },
          'Notification settings updated successfully!',
          'Failed to update notification settings.',
        ),
      );
    }
  };

  const handleGenerateApiKey = async () => {
    setIsGeneratingKey(true);
    try {
      await postRequest('/organization/generate-api-key');
      showSuccessToast('API key generated successfully');
      dispatch(getUserData());
    } catch (error) {
      showErrorToast('Generating API key failed!');
    }
    setIsGeneratingKey(false);
  };

  return (
    <>
      <Alert
        message={`Welcome to Zorba's Marketplace API Integration!`}
        description="Use this link to access our most updated API docs."
        style={{ marginBottom: 30 }}
      />
      {fields.map((field) => (
        <>
          <div key={field.fieldName} className="field-container">
            <div className="description-container">
              <span className="label">{field.label}</span>
              {!(
                selectedField?.fieldName === field.fieldName &&
                field.fieldName === 'globalNotificationRecipients'
              ) && <span className="sub-title">{field.subTitle}</span>}
              {field?.textValue && (
                <span className="textValue">{field.textValue}</span>
              )}
              <div>
                {!(
                  selectedField?.fieldName === field.fieldName &&
                  (field.fieldName === 'globalNotificationRecipients' ||
                    field.fieldName === 'crmApiIntegration')
                ) && (
                  <ZorbaButton
                    variant={BUTTON_VARIANT_LINK}
                    fullWidth={false}
                    className="button notification"
                    onClick={() => handleEdit(field)}
                  >
                    Edit
                  </ZorbaButton>
                )}
              </div>
            </div>
          </div>
          {selectedField?.fieldName === field.fieldName &&
            selectedField.fieldName === 'globalNotificationRecipients' && (
              <div className="form-container">
                <div className="select-container">
                  <ZorbaSelect
                    mode="tags"
                    onChange={(e) =>
                      setSelectedField({
                        ...selectedField,
                        value: e,
                      })
                    }
                    value={selectedField.value}
                    options={notificationsRecipientsOptions}
                  />
                  <span className="global-notifications-hint">
                    The email addresses you enter here will receive every single
                    deal, in addition to the members of your company or the
                    recipients specified in the buy box recipients feature.
                  </span>
                </div>
                <div className="buttons-container">
                  <ZorbaButton
                    fullWidth={false}
                    variant={BUTTON_VARIANT_DARK}
                    onClick={handleSaveGlobalNotificationRecipients}
                    disabled={isLoading}
                    loading={isLoading}
                  >
                    Save
                  </ZorbaButton>
                  <ZorbaButton
                    fullWidth={false}
                    variant={BUTTON_VARIANT_LINK}
                    onClick={handleCancel}
                    disabled={isLoading}
                  >
                    Cancel
                  </ZorbaButton>
                </div>
              </div>
            )}
          {selectedField?.fieldName === field.fieldName &&
            selectedField.fieldName === 'crmApiIntegration' && (
              <Webhooks
                webhooks={selectedField.value}
                setWebhooks={setWebhooks}
                handleCancel={handleCancel}
                handleSave={handleSaveWebhooks}
                isLoading={isLoading}
              />
            )}
          <Divider className="divider" />
        </>
      ))}
      <div>
        <Tooltip
          title={
            TRICON_ORGANIZATION_ID !== organization?._id
              ? `Contact support@getzorba.com or your account manager to get API access to Zorba's inventory`
              : ''
          }
        >
          <span>
            <ZorbaButton
              variant={BUTTON_VARIANT_DARK}
              onClick={handleGenerateApiKey}
              disabled={
                organization?._id !== TRICON_ORGANIZATION_ID || isGeneratingKey
              }
              fullWidth={false}
              loading={isGeneratingKey}
            >
              Generate API Key
            </ZorbaButton>
          </span>
        </Tooltip>
        <Divider />
      </div>
      {organization.apiKeys?.length > 0 && (
        <div className="field-container api-key">
          <div className="description-container">
            <span className="label">Marketplace API Keys</span>
            {isAppLoading || isGeneratingKey ? (
              <Spinner />
            ) : (
              organization.apiKeys.map((key) => (
                <Space.Compact
                  style={{ width: '100%', marginBottom: '8px' }}
                  key={uuid()}
                >
                  <Input value={key} readOnly />
                  <Button
                    icon={
                      <CopyOutlined style={{ minHeight: 50, height: 56 }} />
                    }
                    onClick={() => copy(key)}
                  />
                </Space.Compact>
              ))
            )}
          </div>
        </div>
      )}
      <EditFieldModal
        selectedField={selectedField}
        setSelectedField={setSelectedField}
      >
        <div className="notifications-content-container">
          <span className="sub-title">{selectedField?.subTitle}</span>
          <div className="switch-notification-container">
            <span className="switch-label">Email</span>
            <Switch
              checked={selectedField?.emailValue}
              onChange={handleEmailSwitchChange}
            />
          </div>

          <div className="notification-button-container">
            <ZorbaButton
              fullWidth={false}
              variant={BUTTON_VARIANT_DARK}
              onClick={handleModalClose}
              disabled={isLoading}
              loading={isLoading}
            >
              Close
            </ZorbaButton>
          </div>
        </div>
      </EditFieldModal>
    </>
  );
};
